import type { ApplyArrangement, ArrangeInput, ArrangementDTO, CreateSeatingRule, InspirationPhotoDTO, SeatingRuleDTO } from "@bridewell/shared";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { http } from "./client";
import { keys, RESOURCE, type ResourceModule } from "./keys";

/* The ten list modules (guests, budget, checklist, …): one generic set of hooks, plus the few module-specific extras. */

export interface Headroom { count: number; limit: number | null; remaining: number }

/** One module's rows. The hook and `queryClient.fetchQuery` (the exports) share it, so both read and warm the same cache entry. */
export const moduleListQuery = <T>(weddingId: string, module: ResourceModule) =>
  queryOptions({
    queryKey: keys.module(weddingId, RESOURCE[module]),
    queryFn: async () => (await http.get<T[]>(`/weddings/${weddingId}/${RESOURCE[module]}`)).data,
  });

export function useModuleList<T>(weddingId: string, module: ResourceModule, enabled = true) {
  return useQuery({ ...moduleListQuery<T>(weddingId, module), enabled });
}

/** How many more rows the free plan allows here (`limit: null` on paid plans). */
export function useHeadroom(weddingId: string, module: ResourceModule, enabled = true) {
  return useQuery({
    queryKey: keys.headroom(weddingId, RESOURCE[module]),
    queryFn: async () => (await http.get<Headroom>(`/weddings/${weddingId}/${RESOURCE[module]}/headroom`)).data,
    enabled,
    staleTime: 5_000,
  });
}

/** Invalidate everything derived from one wedding (module lists, dashboard, activity). */
export function useInvalidateWedding(weddingId: string) {
  const qc = useQueryClient();
  return (module?: ResourceModule) => {
    if (module) void qc.invalidateQueries({ queryKey: keys.module(weddingId, RESOURCE[module]) });
    void qc.invalidateQueries({ queryKey: keys.dashboard(weddingId) });
    // Budget lines and vendor dates feed the payments page.
    if (!module || module === "budget" || module === "vendors") void qc.invalidateQueries({ queryKey: keys.payments(weddingId) });
    void qc.invalidateQueries({ queryKey: keys.activity(weddingId) });
  };
}

export function useCreateItem<T, I = Partial<T>>(weddingId: string, module: ResourceModule) {
  const invalidate = useInvalidateWedding(weddingId);
  return useMutation({
    mutationFn: async (input: I) => (await http.post<T>(`/weddings/${weddingId}/${RESOURCE[module]}`, input)).data,
    onSuccess: () => invalidate(module),
  });
}

export function useCreateMany<T, I = Partial<T>>(weddingId: string, module: ResourceModule) {
  const invalidate = useInvalidateWedding(weddingId);
  return useMutation({
    mutationFn: async (items: I[]) => {
      const res = await http.post<T[]>(`/weddings/${weddingId}/${RESOURCE[module]}/batch`, { items });
      return { data: res.data, meta: res.meta as { requested: number; created: number; skipped: number; limit: number | null } };
    },
    onSuccess: () => invalidate(module),
  });
}

export function useUpdateItem<T, I = Partial<T>>(weddingId: string, module: ResourceModule) {
  const qc = useQueryClient();
  const invalidate = useInvalidateWedding(weddingId);
  const key = keys.module(weddingId, RESOURCE[module]);
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: I }) =>
      (await http.patch<T>(`/weddings/${weddingId}/${RESOURCE[module]}/${id}`, patch)).data,
    // Optimistic: a checkbox must flip instantly, even on a slow connection.
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<(T & { id: string })[]>(key);
      if (prev) qc.setQueryData(key, prev.map((r) => (r.id === id ? { ...r, ...(patch as object) } : r)));
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
    onSettled: () => invalidate(module),
  });
}

export interface BatchMeta { requested: number; updated: number; missing: number }

/** Batch PATCH (select mode). Optimistic like single edits; the server's answer replaces the cache on settle. */
export function useUpdateMany<T, I = Partial<T>>(weddingId: string, module: ResourceModule) {
  const qc = useQueryClient();
  const invalidate = useInvalidateWedding(weddingId);
  const key = keys.module(weddingId, RESOURCE[module]);
  return useMutation({
    mutationFn: async ({ ids, patch }: { ids: string[]; patch: I }) => {
      const res = await http.patch<T[]>(`/weddings/${weddingId}/${RESOURCE[module]}/batch`, { ids, patch });
      return { data: res.data, meta: res.meta as unknown as BatchMeta };
    },
    onMutate: async ({ ids, patch }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<(T & { id: string })[]>(key);
      const set = new Set(ids);
      if (prev) qc.setQueryData(key, prev.map((r) => (set.has(r.id) ? { ...r, ...(patch as object) } : r)));
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
    onSettled: () => invalidate(module),
  });
}

/** Optimistic: the row leaves the list at once and comes back if the server refuses. */
export function useDeleteItem(weddingId: string, module: ResourceModule) {
  const qc = useQueryClient();
  const invalidate = useInvalidateWedding(weddingId);
  const key = keys.module(weddingId, RESOURCE[module]);
  return useMutation({
    mutationFn: async (id: string) => { await http.del(`/weddings/${weddingId}/${RESOURCE[module]}/${id}`); },
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<{ id: string }[]>(key);
      if (prev) qc.setQueryData(key, prev.filter((r) => r.id !== id));
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
    onSettled: () => invalidate(module),
  });
}

export function useDeleteMany(weddingId: string, module: ResourceModule) {
  const qc = useQueryClient();
  const invalidate = useInvalidateWedding(weddingId);
  const key = keys.module(weddingId, RESOURCE[module]);
  return useMutation({
    mutationFn: async (ids: string[]) => (await http.post<undefined>(`/weddings/${weddingId}/${RESOURCE[module]}/batch/delete`, { ids })).meta as unknown as BatchMeta,
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<{ id: string }[]>(key);
      const set = new Set(ids);
      if (prev) qc.setQueryData(key, prev.filter((r) => !set.has(r.id)));
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
    onSettled: () => invalidate(module),
  });
}

/* ───────── module-specific extras ───────── */

/** Put guests into one household / take them out (iOS GuestHousehold.group / ungroup). */
export function useHouseholds(weddingId: string) {
  const invalidate = useInvalidateWedding(weddingId);
  const group = useMutation({
    mutationFn: async (input: { ids: string[]; name?: string }) => (await http.post<unknown[]>(`/weddings/${weddingId}/guests/households`, input)).meta as unknown as BatchMeta & { householdId: string },
    onSettled: () => invalidate("guests"),
  });
  const ungroup = useMutation({
    mutationFn: async (ids: string[]) => (await http.post<unknown[]>(`/weddings/${weddingId}/guests/households/ungroup`, { ids })).meta as unknown as BatchMeta,
    onSettled: () => invalidate("guests"),
  });
  return { group, ungroup };
}

/**
 * One photo onto the mood board: multipart with `file` (plus optional `width`, `height`, `caption`, `category`). Each
 * platform builds the FormData (the web from a downscaled Blob, Android from an expo-file-system File); photos are
 * never created through the JSON path. Settles every list the upload changes, success or not (a batch may half-succeed).
 */
export function useUploadInspirationPhoto(weddingId: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateWedding(weddingId);
  return useMutation({
    mutationFn: async (form: FormData) => (await http.upload<InspirationPhotoDTO>(`/weddings/${weddingId}/${RESOURCE.inspiration}`, form)).data,
    onSettled: () => {
      invalidate("inspiration");
      void qc.invalidateQueries({ queryKey: keys.headroom(weddingId, RESOURCE.inspiration) });
      void qc.invalidateQueries({ queryKey: keys.usage });
    },
  });
}

export const seatingRulesQuery = (id: string) =>
  queryOptions({ queryKey: keys.seatingRules(id), queryFn: async () => (await http.get<SeatingRuleDTO[]>(`/weddings/${id}/seating/rules`)).data });

/** Seating rules and auto-arrange: `propose` only suggests; `apply` writes (the server re-checks every seat). */
export function useSeatingArrange(weddingId: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateWedding(weddingId);
  const rulesKey = keys.seatingRules(weddingId);
  const addRule = useMutation({ mutationFn: async (input: CreateSeatingRule) => (await http.post<SeatingRuleDTO>(`/weddings/${weddingId}/seating/rules`, input)).data, onSuccess: () => qc.invalidateQueries({ queryKey: rulesKey }) });
  const removeRule = useMutation({ mutationFn: async (id: string) => { await http.del(`/weddings/${weddingId}/seating/rules/${id}`); }, onSuccess: () => qc.invalidateQueries({ queryKey: rulesKey }) });
  const propose = useMutation({ mutationFn: async (input: ArrangeInput) => (await http.post<ArrangementDTO>(`/weddings/${weddingId}/seating/arrange`, input)).data });
  const apply = useMutation({
    mutationFn: async (assignments: ApplyArrangement["assignments"]) => (await http.post<{ previous: ApplyArrangement["assignments"]; updated: number }>(`/weddings/${weddingId}/seating/arrange/apply`, { assignments })).data,
    onSuccess: () => { invalidate("guests"); invalidate("seating"); },
  });
  return { addRule, removeRule, propose, apply };
}
